import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule, TableRowReorderEvent } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { catchError, of, forkJoin } from 'rxjs';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingTaskTemplate, CreateOnboardingTaskTemplate, OnboardingCategory } from '../../models/onboarding.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

// Fixed tab order: IT → P&C → L&D. Orientation is folded into P&C as a sub-category.
type TabKey = 'IT' | 'PnC' | 'LnD';

interface TabDef {
  key: TabKey;
  label: string;
  matches: (category: string) => boolean;
  defaultCategory: string; // category value pre-selected when adding a new task from this tab
}

// P&C sub-category order is now data-driven (from OnboardingCategory), not hard-coded.

@Component({
  selector: 'app-onboarding-task-templates',
  standalone: true,
  imports: [
    // TableModule provides the p-table component plus its child directives
    // (pReorderableRow, pReorderableRowHandle, pSortableColumn, etc.) — no need to
    // import those directives separately.
    CommonModule, FormsModule, TableModule, Button, Dialog, Select,
    InputText, Checkbox, Tooltip, Tag, ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './onboarding-task-templates.component.html',
  styleUrl: './onboarding-task-templates.component.scss'
})
export class OnboardingTaskTemplatesComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  templates: OnboardingTaskTemplate[] = [];
  loading = true;

  // Tab definitions. Order + labels are refreshed from loaded categories (buildFromCategories);
  // the `matches` predicates stay fixed since they encode which category keys belong to each tab.
  tabs: TabDef[] = [
    { key: 'IT',  label: 'IT',  matches: c => c === 'IT',  defaultCategory: 'IT' },
    { key: 'PnC', label: 'P&C', matches: c => c?.startsWith('PnC.') || c === 'Orientation', defaultCategory: 'PnC.Day1' },
    { key: 'LnD', label: 'L&D', matches: c => c === 'LnD', defaultCategory: 'LnD' }
  ];

  // Loaded categories + derived lookups (built in buildFromCategories).
  categories: OnboardingCategory[] = [];
  pncSubOrder: Record<string, number> = {};   // category key -> order within P&C
  categoryIcon: Record<string, string> = {};  // category key -> icon class

  // Manage-order popup state
  manageOrderVisible = false;
  manageOrderTab: TabKey = 'PnC';
  orderDraft: OnboardingCategory[] = [];       // working copy being dragged/edited
  savingOrder = false;
  iconChoices = [
    'pi-calendar', 'pi-compass', 'pi-comments', 'pi-file', 'pi-cog', 'pi-chart-line',
    'pi-desktop', 'pi-users', 'pi-book', 'pi-briefcase', 'pi-check-square', 'pi-envelope',
    'pi-id-card', 'pi-shield', 'pi-star', 'pi-flag'
  ];

  activeTab: TabKey = 'IT';

  // Per-tab search text and status filter
  tabSearch = '';
  tabStatusFilter: 'all' | 'active' | 'inactive' = 'all';

  statusFilterOptions = [
    { label: 'All Status',   value: 'all' },
    { label: 'Active only',  value: 'active' },
    { label: 'Inactive only', value: 'inactive' }
  ];

  // Category dropdown options (built from loaded categories in buildFromCategories).
  // Fallback defaults below are used until categories load.
  categoryOptions: { label: string; value: string; tab: TabKey }[] = [
    { label: 'IT',                  value: 'IT',                tab: 'IT'  as TabKey },
    { label: 'P&C — Day 1',         value: 'PnC.Day1',          tab: 'PnC' as TabKey },
    { label: 'Orientation',         value: 'Orientation',       tab: 'PnC' as TabKey },
    { label: 'P&C — Communication', value: 'PnC.Communication', tab: 'PnC' as TabKey },
    { label: 'P&C — Documentation', value: 'PnC.Documentation', tab: 'PnC' as TabKey },
    { label: 'P&C — Systems',       value: 'PnC.Systems',       tab: 'PnC' as TabKey },
    { label: 'P&C — Tracking',      value: 'PnC.Tracking',      tab: 'PnC' as TabKey },
    { label: 'L&D',                 value: 'LnD',               tab: 'LnD' as TabKey }
  ];

  emailTypeOptions = [
    { label: '— None —',        value: null },
    { label: 'Welcome Email',   value: 'Welcome' },
    { label: 'All-Hearts Email', value: 'AllHearts' },
    { label: 'Onboarding Email', value: 'Onboarding' }
  ];

  // Dialog state
  displayDialog = false;
  isEditMode = false;
  selectedTemplate: OnboardingTaskTemplate = this.getEmpty();
  fieldTouched: Record<string, boolean> = {};

  // Restrict the Add/Edit dialog's Category dropdown to values relevant to the tab
  // where the user clicked Add. In edit mode all categories remain visible so an
  // Admin can move a task between categories.
  dialogCategoryOptions: { label: string; value: string }[] = [];

  reordering = false; // spinner state while sortOrder patch is in flight

  // Bulk-delete selection state (scoped to the active tab's visible list).
  selectedIds: string[] = [];
  deletingBulk = false;

  constructor(
    private onboardingService: OnboardingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Global page header: only the search box is used (scoped to the active tab in applyFilters);
    // Add and Filter buttons are moved into each tab.
    this.pageHeaderService.setCanShowFilterToggle(false);
    this.pageHeaderService.setPlaceholder('Search tasks in this tab…');
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setCanShowExport(false);

    this.loadTemplates();
    this.loadCategories();

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.tabSearch = v ?? ''; this.refreshDisplay(); });
  }

  loadCategories(): void {
    this.onboardingService.getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as OnboardingCategory[])))
      .subscribe(cats => {
        if (cats && cats.length) {
          this.categories = cats;
          this.buildFromCategories();
        }
      });
  }

  // Rebuild tab order/labels, category dropdown options, sub-category order and icon map
  // from the loaded categories, so the hard-coded order is fully overridden.
  private buildFromCategories(): void {
    const tabCats = this.categories.filter(c => c.isTab).sort((a, b) => a.sortOrder - b.sortOrder);
    const subCats = this.categories.filter(c => !c.isTab);

    // Tab order + labels (keep the fixed matches predicates).
    const matchesByKey: Record<string, (c: string) => boolean> = {
      IT:  c => c === 'IT',
      PnC: c => c?.startsWith('PnC.') || c === 'Orientation',
      LnD: c => c === 'LnD'
    };
    if (tabCats.length) {
      this.tabs = tabCats.map(t => ({
        key: t.key as TabKey,
        label: t.label,
        matches: matchesByKey[t.key] ?? (() => false),
        defaultCategory: t.key === 'PnC' ? 'PnC.Day1' : t.key
      }));
      if (!this.tabs.some(t => t.key === this.activeTab)) {
        this.activeTab = this.tabs[0]?.key ?? 'IT';
      }
    }

    // Category dropdown options in category order (tabs' own key first where applicable).
    this.categoryOptions = [...this.categories]
      .sort((a, b) => (a.tab.localeCompare(b.tab)) || (a.sortOrder - b.sortOrder))
      .filter(c => !c.isTab || c.key === 'IT' || c.key === 'LnD') // tabs IT/LnD are themselves categories
      .map(c => ({ label: c.label, value: c.key, tab: c.tab as TabKey }));

    // P&C sub-category order + icon map.
    this.pncSubOrder = {};
    this.categoryIcon = {};
    for (const c of subCats) {
      this.pncSubOrder[c.key] = c.sortOrder;
    }
    for (const c of this.categories) {
      if (c.icon) this.categoryIcon[c.key] = c.icon;
    }
    this.refreshDisplay();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadTemplates(): void {
    this.loading = true;
    this.onboardingService.getAllTaskTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as OnboardingTaskTemplate[])))
      .subscribe(data => {
        this.templates = data || [];
        this.loading = false;
        this.refreshDisplay();
      });
  }

  // ================= Tab selection =================

  setTab(key: TabKey): void {
    this.activeTab = key;
    this.tabSearch = '';
    this.tabStatusFilter = 'all';
    // Any selection is scoped to the tab you were on — reset when switching tabs.
    this.selectedIds = [];
    this.refreshDisplay();
  }

  getTabCount(key: TabKey): number {
    const def = this.tabs.find(t => t.key === key);
    if (!def) return 0;
    return this.templates.filter(t => def.matches(t.category)).length;
  }

  // ================= Current tab data =================

  /**
   * Tasks visible in the active tab after applying tab-local search and status filter.
   * Sort strategy:
   *   • P&C tab → group tasks by sub-category first (Day 1 → Orientation → Comm → Doc → Sys → Tracking),
   *     then by sortOrder within each sub-category. Sub-categories with no known order fall
   *     to the bottom.
   *   • IT and L&D tabs (single category each) → simple sortOrder ASC.
   */
  get currentTabTasks(): OnboardingTaskTemplate[] {
    const def = this.tabs.find(t => t.key === this.activeTab);
    if (!def) return [];

    let list = this.templates.filter(t => def.matches(t.category));

    const s = this.tabSearch.trim().toLowerCase();
    if (s) list = list.filter(t => t.taskName?.toLowerCase().includes(s));

    if (this.tabStatusFilter === 'active')   list = list.filter(t => t.isDefault);
    if (this.tabStatusFilter === 'inactive') list = list.filter(t => !t.isDefault);

    if (this.activeTab === 'PnC') {
      return [...list].sort((a, b) => {
        const ca = this.pncSubOrder[a.category] ?? 99;
        const cb = this.pncSubOrder[b.category] ?? 99;
        if (ca !== cb) return ca - cb;
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      });
    }
    return [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  // Materialized copy the table binds to. Bound by reference so PrimeNG's row-group metadata
  // recomputes when we reassign it (a getter alone doesn't reliably refresh grouped rows).
  displayTasks: OnboardingTaskTemplate[] = [];

  // Explicit P&C groups in user-defined order — used instead of PrimeNG rowGroupMode so the
  // sub-category header order is fully under our control (PrimeNG's grouping was not honouring it).
  pncGroups: { category: string; tasks: OnboardingTaskTemplate[] }[] = [];

  /** Recompute the table's bound list (new array reference) after any change to order/filters/data. */
  refreshDisplay(): void {
    this.displayTasks = this.currentTabTasks;

    // Build the P&C manual groups from the (already sorted) display list.
    if (this.activeTab === 'PnC') {
      const seen = new Set<string>();
      const orderedCats: string[] = [];
      for (const t of this.displayTasks) {
        if (!seen.has(t.category)) { seen.add(t.category); orderedCats.push(t.category); }
      }
      // Ensure ALL P&C sub-categories appear (even empty ones), in pncSubOrder order.
      const allPncCats = Object.keys(this.pncSubOrder)
        .sort((a, b) => this.pncSubOrder[a] - this.pncSubOrder[b]);
      for (const c of allPncCats) if (!seen.has(c)) { seen.add(c); orderedCats.push(c); }
      orderedCats.sort((a, b) => (this.pncSubOrder[a] ?? 900) - (this.pncSubOrder[b] ?? 900));

      this.pncGroups = orderedCats.map(category => ({
        category,
        tasks: this.displayTasks.filter(t => t.category === category)
      }));
    } else {
      this.pncGroups = [];
    }
  }


  /** Label shown on the "+ Add …" button and dialog headers, based on active tab. */
  get activeTabLabel(): string {
    return this.tabs.find(t => t.key === this.activeTab)?.label ?? '';
  }

  getCategoryLabel(category: string): string {
    const opt = this.categoryOptions.find(o => o.value === category);
    return opt?.label || category;
  }

  // Icon for a category key (from the loaded categories), falling back to a folder icon.
  getCategoryIcon(category: string): string {
    return this.categoryIcon[category] || 'pi-folder';
  }

  // ================= Manage category order popup =================

  openManageOrder(): void {
    // Edit the sub-categories of the active tab if it's P&C; otherwise edit the top-level tabs.
    this.manageOrderTab = this.activeTab === 'PnC' ? 'PnC' : 'PnC';
    this.buildOrderDraft('PnC');
    this.manageOrderVisible = true;
  }

  // Which scope is being reordered in the popup: 'tabs' or the P&C sub-categories.
  orderScope: 'tabs' | 'pncSub' = 'pncSub';

  setOrderScope(scope: 'tabs' | 'pncSub'): void {
    this.orderScope = scope;
    this.buildOrderDraft(scope);
  }

  private buildOrderDraft(scope: 'tabs' | 'pncSub' | 'PnC'): void {
    const useTabs = scope === 'tabs';
    this.orderScope = useTabs ? 'tabs' : 'pncSub';
    this.orderDraft = this.categories
      .filter(c => useTabs ? c.isTab : (!c.isTab && c.tab === 'PnC'))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(c => ({ ...c }));
  }

  onOrderReorder(): void {
    // The p-table reorder mutates orderDraft array order; renumber sortOrder to match.
    this.orderDraft.forEach((c, i) => (c.sortOrder = i));
  }

  moveOrderItem(index: number, dir: -1 | 1): void {
    const target = index + dir;
    if (target < 0 || target >= this.orderDraft.length) return;
    const arr = this.orderDraft;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    this.onOrderReorder();
  }

  saveOrder(): void {
    this.savingOrder = true;
    const items = this.orderDraft.map((c, i) => ({ id: c.id, sortOrder: i, icon: c.icon }));
    this.onboardingService.reorderCategories(items)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cats) => {
          this.categories = cats;
          this.buildFromCategories();
          this.savingOrder = false;
          this.manageOrderVisible = false;
          this.messageService.add({ severity: 'success', summary: 'Order saved', detail: 'Category order updated.' });
        },
        error: () => {
          this.savingOrder = false;
          this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save the order.' });
        }
      });
  }

  getEmailTypeLabel(emailType?: string | null): string {
    if (!emailType) return '';
    return this.emailTypeOptions.find(o => o.value === emailType)?.label || emailType;
  }

  // Reorder tasks within one P&C sub-category group (manual grouping). Renumbers sortOrder for
  // that category's tasks and persists the changed ones.
  onGroupRowReorder(category: string, event: TableRowReorderEvent): void {
    const group = this.pncGroups.find(g => g.category === category);
    if (!group) return;

    // group.tasks was already mutated into the new order by the p-table reorder.
    const updates: OnboardingTaskTemplate[] = [];
    group.tasks.forEach((task, i) => {
      if (task.sortOrder !== i) {
        task.sortOrder = i;
        updates.push(task);
      }
    });
    if (updates.length === 0) return;

    this.reordering = true;
    forkJoin(updates.map(u => this.onboardingService.updateTaskTemplate(u.id, u)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          results.forEach(r => {
            const idx = this.templates.findIndex(t => t.id === r.id);
            if (idx >= 0) this.templates[idx] = r;
          });
          this.reordering = false;
          this.refreshDisplay();
          this.messageService.add({ severity: 'success', summary: 'Reordered', detail: `${results.length} task(s) updated`, life: 1500 });
        },
        error: () => {
          this.reordering = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save order. Reloading…' });
          this.loadTemplates();
        }
      });
  }

  // ================= Drag-drop reordering (PrimeNG reorderableRows) =================

  /**
   * User drags a row within the current tab. PrimeNG mutates the source array,
   * but our template renders from a computed getter — so we compute the new order,
   * update sortOrder on all affected rows, and PATCH each to the backend.
   * Only the small subset that changed positions is written.
   */
  onRowReorder(event: TableRowReorderEvent): void {
    // PrimeNG's TableRowReorderEvent types dragIndex / dropIndex as optional. In practice
    // both are set on a real drop; guard against undefined to satisfy the compiler.
    const dragIndex = event.dragIndex;
    const dropIndex = event.dropIndex;
    if (dragIndex === undefined || dropIndex === undefined) return;
    if (dragIndex === dropIndex) return;

    const before = this.currentTabTasks; // sorted view before we mutate sortOrder
    const moving = before[dragIndex];
    const target = before[dropIndex];
    if (!moving || !target) return;

    // In the P&C tab, tasks are grouped by sub-category. Reordering across sub-categories
    // (e.g. moving a Day 1 task into Orientation) would change the task's category — surprising
    // and destructive. Reject the drop and inform the user.
    if (moving.category !== target.category) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot move across sub-categories',
        detail: 'Drag within the same section only (e.g. rearrange Day 1 tasks amongst themselves).',
        life: 3000
      });
      return;
    }

    // Only reorder within the moved task's sub-category. Peers = the same-category tasks
    // in current display order.
    const peers = before.filter(t => t.category === moving.category);
    const oldPos = peers.indexOf(moving);
    const newPos = peers.indexOf(target);
    if (oldPos === newPos) return;

    const peersReordered = [...peers];
    peersReordered.splice(oldPos, 1);
    peersReordered.splice(newPos, 0, moving);

    // Write new sortOrder values only for peers whose position actually changed.
    const updates: OnboardingTaskTemplate[] = [];
    peersReordered.forEach((task, newIdx) => {
      if (task.sortOrder !== newIdx) {
        task.sortOrder = newIdx;
        updates.push(task);
      }
    });

    if (updates.length === 0) return;

    this.reordering = true;
    forkJoin(updates.map(u => this.onboardingService.updateTaskTemplate(u.id, u)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          // Sync back — copy authoritative sortOrder from server results
          results.forEach(r => {
            const idx = this.templates.findIndex(t => t.id === r.id);
            if (idx >= 0) this.templates[idx] = r;
          });
          this.reordering = false;
          this.refreshDisplay();
          this.messageService.add({ severity: 'success', summary: 'Reordered', detail: `${results.length} task${results.length > 1 ? 's' : ''} updated`, life: 1500 });
        },
        error: () => {
          this.reordering = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save new order. Reloading…' });
          this.loadTemplates();
        }
      });
  }

  // ================= Bulk selection (scoped to the current tab's visible list) =================

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  /** True when every visible task in the current tab is selected. */
  isAllVisibleSelected(): boolean {
    const visible = this.currentTabTasks;
    return visible.length > 0 && visible.every(t => this.selectedIds.includes(t.id));
  }

  toggleSelect(id: string): void {
    const idx = this.selectedIds.indexOf(id);
    if (idx >= 0) this.selectedIds.splice(idx, 1);
    else this.selectedIds.push(id);
  }

  toggleSelectAllVisible(checked: boolean): void {
    const visibleIds = this.currentTabTasks.map(t => t.id);
    if (checked) {
      // Union — don't drop selections from other tabs (though we reset on tab switch anyway)
      this.selectedIds = Array.from(new Set([...this.selectedIds, ...visibleIds]));
    } else {
      this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id));
    }
  }

  clearSelection(): void {
    this.selectedIds = [];
  }

  /** Bulk delete confirmation + parallel API calls. */
  deleteSelected(): void {
    if (this.selectedIds.length === 0) return;

    const count = this.selectedIds.length;
    this.confirmationService.confirm({
      header: 'Delete Selected Tasks',
      message: `Delete ${count} selected task${count > 1 ? 's' : ''}? This cannot be undone. In-progress onboarding records that already have these tasks are not affected.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deletingBulk = true;
        const ids = [...this.selectedIds];
        forkJoin(ids.map(id => this.onboardingService.deleteTaskTemplate(id)))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.templates = this.templates.filter(t => !ids.includes(t.id));
              this.selectedIds = [];
              this.deletingBulk = false;
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `${ids.length} task${ids.length > 1 ? 's' : ''} deleted` });
            },
            error: () => {
              this.deletingBulk = false;
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'One or more deletes failed. Reloading…' });
              this.loadTemplates();
            }
          });
      }
    });
  }

  // ================= Add / Edit dialog =================

  showAddDialog(): void {
    const def = this.tabs.find(t => t.key === this.activeTab);
    this.selectedTemplate = this.getEmpty();
    this.selectedTemplate.category = def?.defaultCategory ?? '';
    // Restrict category dropdown to just this tab's categories when adding — one less field to think about
    this.dialogCategoryOptions = this.categoryOptions.filter(o => o.tab === this.activeTab).map(o => ({ label: o.label, value: o.value }));
    this.isEditMode = false;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  showEditDialog(template: OnboardingTaskTemplate): void {
    this.selectedTemplate = { ...template };
    // In edit mode allow all categories (admin can move a task between categories)
    this.dialogCategoryOptions = this.categoryOptions.map(o => ({ label: o.label, value: o.value }));
    this.isEditMode = true;
    this.fieldTouched = {};
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
  }

  saveTemplate(): void {
    this.fieldTouched['taskName'] = true;
    this.fieldTouched['category'] = true;
    if (!this.selectedTemplate.taskName.trim() || !this.selectedTemplate.category) return;

    if (this.isEditMode) {
      this.onboardingService.updateTaskTemplate(this.selectedTemplate.id, this.selectedTemplate)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            const idx = this.templates.findIndex(t => t.id === updated.id);
            if (idx >= 0) this.templates[idx] = updated;
            this.messageService.add({ severity: 'success', summary: 'Updated', detail: this.propagationDetail('Task updated', updated.propagatedToCount) });
            this.hideDialog();
          },
          error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update' }); }
        });
    } else {
      // New task lands at the end of its category — auto-compute sortOrder so users never touch it
      const siblings = this.templates.filter(t => t.category === this.selectedTemplate.category);
      const nextOrder = siblings.length === 0
        ? 0
        : Math.max(...siblings.map(s => s.sortOrder ?? 0)) + 1;

      const dto: CreateOnboardingTaskTemplate = {
        taskName: this.selectedTemplate.taskName,
        category: this.selectedTemplate.category,
        department: this.selectedTemplate.department,
        isDefault: this.selectedTemplate.isDefault,
        sortOrder: nextOrder,
        emailType: this.selectedTemplate.emailType || undefined
      };
      this.onboardingService.createTaskTemplate(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.templates.push(created);
            this.messageService.add({ severity: 'success', summary: 'Created', detail: this.propagationDetail('Task created', created.propagatedToCount) });
            this.hideDialog();
          },
          error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create' }); }
        });
    }
  }

  // ================= Row actions =================

  toggleActive(template: OnboardingTaskTemplate): void {
    const activating = !template.isDefault;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${template.taskName}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
    const updated = { ...template, isDefault: !template.isDefault };
    this.onboardingService.updateTaskTemplate(template.id, updated)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (returned) => {
          const idx = this.templates.findIndex(t => t.id === returned.id);
          if (idx >= 0) this.templates[idx] = returned;
          const base = `"${template.taskName}" ${returned.isDefault ? 'activated' : 'deactivated'}`;
          const detail = returned.isDefault
            ? this.propagationDetail(base, returned.propagatedToCount)
            : this.removalDetail(base, returned.removedFromCount);
          this.messageService.add({
            severity: 'success',
            summary: returned.isDefault ? 'Activated' : 'Deactivated',
            detail
          });
        },
        error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update' }); }
      });
      }
    });
  }

  deleteTemplate(template: OnboardingTaskTemplate): void {
    this.confirmationService.confirm({
      message: `Delete task "${template.taskName}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.onboardingService.deleteTaskTemplate(template.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.templates = this.templates.filter(t => t.id !== template.id);
              this.messageService.add({ severity: 'success', summary: 'Deleted' });
            },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete' }); }
          });
      }
    });
  }

  seedDefaults(): void {
    this.confirmationService.confirm({
      message: 'Seed default BISTEC Global onboarding tasks? This only works if the templates table is empty.',
      header: 'Seed Defaults',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.onboardingService.seedDefaultTemplates()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Seeded', detail: 'Default templates created' }); this.loadTemplates(); },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to seed' }); }
          });
      }
    });
  }

  // ================= Helpers =================

  private propagationDetail(base: string, count?: number): string {
    if (!count || count === 0) return base;
    return `${base} — added to ${count} in-progress onboarding record${count > 1 ? 's' : ''}`;
  }

  private removalDetail(base: string, count?: number): string {
    if (!count || count === 0) return base;
    return `${base} — removed from ${count} in-progress onboarding record${count > 1 ? 's' : ''}`;
  }

  private getEmpty(): OnboardingTaskTemplate {
    return { id: '', taskName: '', category: '', department: '', isDefault: true, sortOrder: 0, emailType: undefined };
  }
}
